import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { api } from '../../lib/api';
import type { User } from '../../lib/types';
import Modal from '../../components/ui/Modal';
import CollectionLabelsTab from './CollectionLabelsTab';
import AiSettingsTab from './AiSettingsTab';
import ContextCard from '../../components/ui/ContextCard';
import PageContainer from '../../components/ui/PageContainer';
import PageHeader from '../../components/ui/PageHeader';
import PublishBanner from '../../components/ui/PublishBanner';

type Tab = 'publicacion' | 'secciones' | 'usuarios' | 'ia';

const CONTEXT: Record<Tab, { title: string; description: string; detail: string }> = {
  publicacion: {
    title: '¿Qué son los ajustes de publicación?',
    description: 'Definen la identidad principal del sitio, su dominio y la conexión utilizada para publicar cambios.',
    detail: 'Modifica los datos de publicación solo si cambió la configuración de alojamiento o dominio.',
  },
  secciones: {
    title: '¿Qué son las secciones de contenido?',
    description: 'Son grupos de contenido repetible de tu negocio, como Propiedades, Equipo, Productos, Servicios o Blog.',
    detail: 'Aquí solo cambias sus nombres visibles. El contenido de cada sección se administra desde el menú lateral.',
  },
  usuarios: {
    title: '¿Qué son los usuarios?',
    description: 'Son las personas que pueden entrar al panel para consultar o modificar la información del negocio.',
    detail: 'Asigna acceso únicamente a personas autorizadas y revisa su nivel de permisos antes de crearlas.',
  },
  ia: {
    title: '¿Qué es la configuración de IA?',
    description: 'Controla los modelos, los límites de uso y las instrucciones que sigue la IA al crear textos e imágenes.',
    detail: 'Estos ajustes aplican a todos los negocios de la plataforma. Cambia las instrucciones solo si sabes lo que haces.',
  },
};

// ─── AddUserModal ──────────────────────────────────────────────────────────────
function AddUserModal({ onSave, onCancel }: { onSave: (u: User) => void; onCancel: () => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<'admin' | 'editor'>('editor');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSave() {
    if (!email || !password) return;
    setSaving(true);
    setError(null);
    try {
      const user = await api.settings.createUser({ email, password, role });
      onSave(user);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal title="Nuevo usuario" onClose={onCancel}>
      <div className="space-y-4">
        <div>
          <label className="block text-sm text-ui-text-muted mb-1">Email</label>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="usuario@ejemplo.com"
            className="w-full bg-ui-bg border border-ui-border rounded px-3 py-2 text-sm text-ui-text focus:outline-none focus:border-ui-accent" />
        </div>
        <div>
          <label className="block text-sm text-ui-text-muted mb-1">Contraseña inicial</label>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
            className="w-full bg-ui-bg border border-ui-border rounded px-3 py-2 text-sm text-ui-text focus:outline-none focus:border-ui-accent" />
        </div>
        <div>
          <label className="block text-sm text-ui-text-muted mb-1">Rol</label>
          <select value={role} onChange={(e) => setRole(e.target.value as 'admin' | 'editor')}
            className="w-full bg-ui-bg border border-ui-border rounded px-3 py-2 text-sm text-ui-text focus:outline-none focus:border-ui-accent">
            <option value="editor">Editor</option>
            <option value="admin">Admin</option>
          </select>
        </div>
        {error && <p className="text-sm text-ui-danger">{error}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onCancel} className="px-3 py-1.5 text-sm text-ui-text-muted hover:text-ui-text">Cancelar</button>
          <button onClick={handleSave} disabled={saving || !email || !password}
            className="px-4 py-1.5 bg-ui-accent text-ui-text-inverse text-sm rounded-md disabled:opacity-50">
            {saving ? 'Creando…' : 'Crear usuario'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ─── SettingsPage ──────────────────────────────────────────────────────────────
export default function SettingsPage() {
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState<Tab>((searchParams.get('tab') as Tab) ?? 'publicacion');
  const [users, setUsers] = useState<User[]>([]);
  const [saving, setSaving] = useState(false);
  const [needsPublish, setNeedsPublish] = useState(false);
  const [showAddUser, setShowAddUser] = useState(false);
  const [profileEmail, setProfileEmail] = useState('');
  const [profileRole, setProfileRole] = useState('');

  // Site info (tab Publicación)
  const [siteName, setSiteName] = useState('');
  const [siteSlug, setSiteSlug] = useState('');
  const [siteDomain, setSiteDomain] = useState('');
  const [siteDeployHook, setSiteDeployHook] = useState('');
  const [sitePagesProject, setSitePagesProject] = useState('');
  const [siteDeployAccountId, setSiteDeployAccountId] = useState('');
  const [siteDeployToken, setSiteDeployToken] = useState('');
  const [siteDeployTokenSet, setSiteDeployTokenSet] = useState(false);
  const [siteBookingTimezone, setSiteBookingTimezone] = useState('America/Mexico_City');

  useEffect(() => {
    api.auth.me().then(({ user }) => {
      if (!user) return;
      setProfileEmail(user.email);
      setProfileRole(user.role);
    }).catch(console.error);
    api.settings.getSite().then((s) => {
      if (!s) return;
      setSiteName(s.name);
      setSiteSlug(s.slug);
      setSiteDomain(s.domain ?? '');
      setSiteDeployHook(s.deployHookUrl ?? '');
      setSitePagesProject(s.pagesProject ?? '');
      setSiteDeployAccountId(s.deployAccountId ?? '');
      setSiteDeployTokenSet(Boolean(s.deployTokenSet));
      setSiteDeployToken('');
      setSiteBookingTimezone(s.bookingTimezone || 'America/Mexico_City');
    }).catch(console.error);
  }, []);

  useEffect(() => {
    if (profileRole === 'admin' || profileRole === 'saas_admin') {
      api.settings.listUsers().then(setUsers).catch(console.error);
    }
  }, [profileRole]);

  async function saveSite() {
    setSaving(true);
    try {
      await api.settings.updateSite({
        name: siteName,
        domain: siteDomain,
        deployHookUrl: siteDeployHook,
        pagesProject: sitePagesProject,
        deployAccountId: siteDeployAccountId,
        ...(siteDeployToken !== '' && { deployToken: siteDeployToken }),
        bookingTimezone: siteBookingTimezone,
      });
      if (siteDeployToken !== '') {
        setSiteDeployTokenSet(true);
        setSiteDeployToken('');
      }
      setNeedsPublish(true);
    } finally {
      setSaving(false);
    }
  }

  async function deleteUser(id: number) {
    if (!confirm('¿Eliminar usuario?')) return;
    await api.settings.deleteUser(id);
    setUsers((prev) => prev.filter((u) => u.id !== id));
  }

  const isAdmin = profileRole === 'admin' || profileRole === 'saas_admin';
  const isSaasAdmin = profileRole === 'saas_admin';

  const tabs: { id: Tab; label: string }[] = [
    ...(isAdmin ? [{ id: 'publicacion' as Tab, label: 'Publicación' }] : []),
    { id: 'secciones' as Tab, label: 'Secciones' },
    ...(isAdmin ? [{ id: 'usuarios' as Tab, label: 'Usuarios' }] : []),
    ...(isSaasAdmin ? [{ id: 'ia' as Tab, label: 'IA' }] : []),
  ];

  return (
    <PageContainer>
      <PageHeader title="Ajustes" />

      <div className="flex overflow-x-auto [&::-webkit-scrollbar]:hidden border-b border-ui-border">
        {tabs.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`shrink-0 whitespace-nowrap px-4 py-2 text-sm border-b-2 transition-colors ${
              tab === t.id ? 'border-ui-accent text-ui-text' : 'border-transparent text-ui-text-muted hover:text-ui-text'
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      <ContextCard {...CONTEXT[tab]} />

      {tab !== 'usuarios' && tab !== 'ia' && (
        <PublishBanner needsPublish={needsPublish} onPublished={() => setNeedsPublish(false)} />
      )}

      {/* ── Publicación ────────────────────────────────────────────────────────── */}
      {tab === 'publicacion' && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-ui-text-muted mb-1">Nombre del sitio</label>
            <input value={siteName} onChange={(e) => setSiteName(e.target.value)} placeholder="Mi Empresa"
              className="w-full bg-ui-bg border border-ui-border rounded px-3 py-2 text-sm text-ui-text focus:outline-none focus:border-ui-accent" />
          </div>
          <div>
            <label className="block text-sm text-ui-text-muted mb-1">Slug</label>
            <input value={siteSlug} readOnly
              className="w-full bg-ui-bg border border-ui-border rounded px-3 py-2 text-sm text-ui-text-subtle font-mono cursor-not-allowed" />
            <p className="text-sm text-ui-text-subtle mt-1">El slug no se puede cambiar una vez creado.</p>
          </div>
          <div>
            <label className="block text-sm text-ui-text-muted mb-1">Dominio personalizado</label>
            <input value={siteDomain} onChange={(e) => setSiteDomain(e.target.value)} placeholder="www.miempresa.com"
              className="w-full bg-ui-bg border border-ui-border rounded px-3 py-2 text-sm text-ui-text focus:outline-none focus:border-ui-accent" />
          </div>
          <div>
            <label className="block text-sm text-ui-text-muted mb-1">Zona horaria principal</label>
            <select value={siteBookingTimezone} onChange={(e) => setSiteBookingTimezone(e.target.value)}
              className="w-full bg-ui-bg border border-ui-border rounded px-3 py-2 text-sm text-ui-text focus:outline-none focus:border-ui-accent">
              <option value="America/Tijuana">Tijuana</option>
              <option value="America/Hermosillo">Sonora</option>
              <option value="America/Mazatlan">Sinaloa</option>
              <option value="America/Chihuahua">Chihuahua</option>
              <option value="America/Monterrey">Monterrey</option>
              <option value="America/Mexico_City">Centro de México</option>
              <option value="America/Cancun">Quintana Roo</option>
            </select>
            <p className="text-sm text-ui-text-subtle mt-1">Se usa en las citas cuando no se ha elegido una sucursal con un horario diferente.</p>
          </div>
          <div className="pt-2 border-t border-ui-border">
            <h3 className="text-sm font-medium text-ui-text mb-1">Direct Upload (recomendado)</h3>
            <p className="text-sm text-ui-text-subtle mb-3">
              El build corre en tu cuenta (GitHub Actions) y sube el sitio a la cuenta Cloudflare del cliente.
              Si rellenas estos campos, el publish usa Direct Upload; si no, cae al Deploy Hook.
            </p>
          </div>
          <div>
            <label className="block text-sm text-ui-text-muted mb-1">Pages project (cuenta destino)</label>
            <input value={sitePagesProject} onChange={(e) => setSitePagesProject(e.target.value)} placeholder="palacete-site"
              className="w-full bg-ui-bg border border-ui-border rounded px-3 py-2 text-sm text-ui-text font-mono placeholder-ui-text-subtle focus:outline-none focus:border-ui-accent" />
          </div>
          <div>
            <label className="block text-sm text-ui-text-muted mb-1">Account ID destino</label>
            <input value={siteDeployAccountId} onChange={(e) => setSiteDeployAccountId(e.target.value)} placeholder="ID de la cuenta Cloudflare del cliente"
              className="w-full bg-ui-bg border border-ui-border rounded px-3 py-2 text-sm text-ui-text font-mono placeholder-ui-text-subtle focus:outline-none focus:border-ui-accent" />
          </div>
          <div>
            <label className="block text-sm text-ui-text-muted mb-1">
              Deploy token (Pages:Edit de la cuenta destino)
              {siteDeployTokenSet && <span className="ml-2 text-emerald-500">● configurado</span>}
            </label>
            <input type="password" value={siteDeployToken} onChange={(e) => setSiteDeployToken(e.target.value)}
              placeholder={siteDeployTokenSet ? '•••••••• (déjalo vacío para no cambiarlo)' : 'Pega el token aquí'}
              autoComplete="new-password"
              className="w-full bg-ui-bg border border-ui-border rounded px-3 py-2 text-sm text-ui-text font-mono placeholder-ui-text-subtle focus:outline-none focus:border-ui-accent" />
            <p className="text-sm text-ui-text-subtle mt-1">Se cifra al guardar y nunca se vuelve a mostrar.</p>
          </div>
          <div className="pt-2 border-t border-ui-border">
            <label className="block text-sm text-ui-text-muted mb-1">Deploy Hook URL (legacy)</label>
            <input value={siteDeployHook} onChange={(e) => setSiteDeployHook(e.target.value)}
              placeholder="https://api.cloudflare.com/client/v4/pages/webhooks/…"
              className="w-full bg-ui-bg border border-ui-border rounded px-3 py-2 text-sm text-ui-text font-mono placeholder-ui-text-subtle focus:outline-none focus:border-ui-accent" />
            <p className="text-sm text-ui-text-subtle mt-1">Solo se usa si no hay config de Direct Upload.</p>
          </div>
          <button onClick={saveSite} disabled={saving}
            className="px-4 py-2 bg-ui-accent text-ui-text-inverse text-sm rounded-md disabled:opacity-50 hover:opacity-90">
            {saving ? 'Guardando…' : 'Guardar cambios'}
          </button>
        </div>
      )}

      {/* ── Secciones ──────────────────────────────────────────────────────────── */}
      {tab === 'secciones' && <CollectionLabelsTab onSaved={() => setNeedsPublish(true)} />}

      {/* ── IA (solo SaaS owner) ──────────────────────────────────────────────── */}
      {tab === 'ia' && <AiSettingsTab />}

      {/* ── Usuarios ───────────────────────────────────────────────────────────── */}
      {tab === 'usuarios' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-ui-text-muted">{users.length} usuario(s) registrado(s)</p>
            <button onClick={() => setShowAddUser(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-ui-accent text-ui-text-inverse text-sm rounded-md hover:opacity-90">
              <Plus size={14} /> Añadir usuario
            </button>
          </div>
          <div className="bg-ui-surface rounded-lg border border-ui-border divide-y divide-ui-border">
            {users.map((u) => (
              <div key={u.id} className="flex items-center justify-between px-4 py-3">
                <div>
                  <p className="text-sm text-ui-text">{u.email}</p>
                  <p className="text-xs text-ui-text-subtle capitalize">{u.role}</p>
                </div>
                {u.email !== profileEmail && (
                  <button onClick={() => deleteUser(u.id)} className="text-sm text-ui-danger hover:opacity-80">
                    Eliminar
                  </button>
                )}
              </div>
            ))}
            {users.length === 0 && (
              <p className="px-4 py-6 text-sm text-ui-text-subtle text-center">Sin usuarios.</p>
            )}
          </div>
        </div>
      )}

      {showAddUser && (
        <AddUserModal
          onSave={(u) => { setUsers((prev) => [...prev, u]); setShowAddUser(false); }}
          onCancel={() => setShowAddUser(false)}
        />
      )}
    </PageContainer>
  );
}
